import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X } from 'lucide-react';

const DISMISSED_KEY = 'iris_pwa_dismissed';

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;
    // Only nudge on mobile
    const smallScreen = window.matchMedia('(max-width: 768px)').matches;
    if (!smallScreen) return;

    const ua = window.navigator.userAgent;
    const iosDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const alreadyInstalled = window.matchMedia('(display-mode: standalone)').matches
                             || window.navigator.standalone;
    if (alreadyInstalled) return;

    if (iosDevice) {
      setIsIOS(true);
      const t = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(t);
    }

    const onEvt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onEvt);
    return () => window.removeEventListener('beforeinstallprompt', onEvt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          className="fixed bottom-28 left-3 right-3 z-40 md:hidden"
          data-testid="pwa-prompt"
        >
          <div className="glass-strong p-4 flex items-center gap-3 shadow-2xl">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center flex-shrink-0 overflow-hidden p-2">
              <img src="/iris-icon.svg" alt="" className="w-full h-full object-contain"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">Install Iris on your phone</div>
              <div className="text-[11px] text-slate-500 leading-tight mt-0.5">
                {isIOS
                  ? "Tap Share → 'Add to Home Screen' for app-like access."
                  : "One-tap install — quicker access, works offline for basic browsing."}
              </div>
            </div>
            {!isIOS && (
              <button onClick={install} data-testid="pwa-install-btn"
                      className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 flex-shrink-0">
                <Download size={12}/> Install
              </button>
            )}
            <button onClick={dismiss} data-testid="pwa-dismiss-btn"
                    className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 flex-shrink-0">
              <X size={14}/>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
